import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Award,
  GraduationCap,
  Lightbulb,
  BookOpen,
  Layers,
  Rocket,
} from "lucide-react";

/* ================= BENEFITS DATA ================= */

const benefits = [
  {
    icon: Award,
    title: "Letter of Recommendation — Performance-Based",
    desc: "Letters of Recommendation may be considered for interns who demonstrate strong performance, meaningful contributions, and professional commitment during the internship. Issuance is at the discretion of the authorised recommender and is not guaranteed upon completion.",
  },
  {
    icon: GraduationCap,
    title: "Internship Completion Certificate",
    desc: "Receive a certificate upon meeting the programme's completion requirements, subject to the applicable internship policy.",
  },
  {
    icon: Lightbulb,
    title: "Practical Projects & Skill Development",
    desc: "Develop problem-solving, communication, teamwork, and analytical skills through assigned projects and activities aligned with your internship role.",
  },
  {
    icon: BookOpen,
    title: "Research & Academic Learning",
    desc: "Explore healthcare and life-science topics through guided learning, research-oriented activities, and academic resources relevant to your internship.",
  },
  {
    icon: Layers,
    title: "Expert Mentorship & Career Guidance",
    desc: "Receive guidance and feedback from designated mentors to strengthen your skills, understand your field, and explore future career opportunities.",
  },
  {
    icon: Rocket,
    title: "Professional Development",
    desc: "Improve your presentation, documentation, and professional communication skills through structured internship tasks and feedback.",
  },
];

/* ================= BENEFIT CARD ================= */

function BenefitCard({ benefit, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const isLeft = index % 2 === 0;
  const Icon = benefit.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isLeft ? -80 : 80, y: 30 }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="relative h-full"
      style={{ width: "100%" }}
    >
      <motion.div
        whileHover={{ y: -6, scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="relative group h-full"
      >
        {/* Brand Glow */}
        <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-brand-green/30 to-brand-blue/30 opacity-0 group-hover:opacity-40 blur-2xl transition-all duration-700" />

        <div className="relative glass-card h-full rounded-2xl p-5 md:p-6 border border-border/40 shadow-md group-hover:shadow-xl transition-all duration-500">

          {/* Accent Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className={`absolute top-0 ${
              isLeft
                ? "left-0 origin-left rounded-tl-2xl"
                : "right-0 origin-right rounded-tr-2xl"
            } h-1 w-20 bg-gradient-to-r from-brand-green to-brand-blue`}
          />

          <div className="flex h-full items-start gap-4">

            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={isInView ? { scale: 1, rotate: 0 } : {}}
              transition={{
                type: "spring",
                stiffness: 160,
                damping: 12,
                delay: 0.2,
              }}
              className="shrink-0"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-green to-brand-blue flex items-center justify-center shadow-lg">
                <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
              </div>
            </motion.div>

            {/* Content */}
            <div className="flex-1">
              <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-brand-green/70 mb-1.5">
                Benefit {index + 1}
              </span>

              <h3 className="text-lg md:text-xl font-black text-foreground tracking-tight leading-snug">
                {benefit.title}
              </h3>

              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {benefit.desc}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ================= MAIN COMPONENT ================= */

export default function BenefitsRoadmap() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  return (
    <section className="organic-bg relative py-16 md:py-20 px-4 overflow-hidden">

      {/* Internship Background System */}
      <div className="floating-orb orb-1" />
      <div className="floating-orb orb-2" />
      <div className="floating-orb orb-3" />
      <div className="floating-orb orb-4" />
      <div className="mesh-overlay" />
      <div className="circuit-pattern" />

      {/* Header */}
      <div
        ref={headerRef}
        className="relative z-10 max-w-3xl mx-auto text-center mb-10 md:mb-12"
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="inline-block text-xs font-bold uppercase tracking-[0.35em] text-brand-green/70 mb-6"
        >
          Your Growth Path
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display text-5xl md:text-6xl font-black tracking-tight leading-[1.1]"
        >
          Why Join
          <br />
          <span className="bg-gradient-to-r from-brand-green via-brand-blue to-brand-green bg-clip-text text-transparent">
            Our Internship?
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 text-muted-foreground text-lg md:text-xl max-w-lg mx-auto"
        >
          Structured learning and project activities to support your professional development.
        </motion.p>
      </div>

      {/* Roadmap */}
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} benefit={benefit} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
